<template>
  <form class="create-entity-container" @submit.prevent="handleSubmit">
    <FriedInput
      v-model.trim="formData.name"
      v-autofocus
      class="create-entity-input"
      data-external="create-entity-company-name-input"
      :label="$t('company-name')"
      :help-message="$t('you-can-always-change-the-company-name-later')"
      :error-message="formErrorName"
      @blur="validateForm"
    />
    <FriedSelect
      class="create-entity-input"
      :options="countryItems"
      :model-value="selectedCountry"
      :label="$t('country')"
      option-label="labelWithFlag"
      :searchable="true"
      :empty-state-text="$t('no-items-found')"
      :help-message="$t('connected-to-your-sendouts-currency-and-timezone')"
      @update:model-value="handleCountryChange"
    />
    <FriedSelect
      class="create-entity-input"
      :options="datacenterItems"
      :model-value="selectedDatacenter"
      :label="$t('datacenter')"
      option-label="name"
      :empty-state-text="$t('no-items-found')"
      :help-message="$t('the-physical-location-of-your-document-storage')"
      @update:model-value="handleDatacenterChange"
    />
  </form>
</template>

<script lang="ts">
import { countries } from '@getaccept/lib-shared-new/src/countries/constants/countries';
import type { Country } from '@getaccept/lib-shared-new/src/countries/types/country';
import { DatacenterId } from '@getaccept/lib-shared-new/src/datacenter/enums/datacenter-id';
import { datacenters } from '@getaccept/lib-shared-new/src/datacenter/constants/datacenters';
import type { PropType } from 'vue';
import { computed, ref, defineComponent, onMounted, watch } from 'vue';
import type { CountryWithLabelFlag } from '@getaccept/lib-shared-new/src/countries/types/country-with-label-flag';
import { useI18n } from 'vue-i18n';
import type { CreateEntityFormData } from '../types/create-entity-form-data';
import { DatacenterHelper } from '../../helpers/datacenter.helper';

export default defineComponent({
  name: 'CreateEntityForm',
  props: {
    initialFormData: {
      type: Object as PropType<CreateEntityFormData>,
      default: () => ({
        name: '',
        countryCode: '',
        timezone: '',
        datacenter: DatacenterId.USOregon,
      }),
    },
  },
  emits: ['submit'],
  setup(props, context) {
    const { t } = useI18n();
    const formData = ref({
      name: '',
      countryCode: '',
      timezone: '',
      datacenter: DatacenterId.USOregon,
    });
    const formErrorName = ref('');

    watch(
      () => formData.value.name,
      () => (formErrorName.value = '')
    );

    const countryItems = computed(() =>
      countries.map(
        (country): CountryWithLabelFlag => ({
          ...country,
          labelWithFlag: `${country.flag} ${country.label}`,
        })
      )
    );

    const selectedCountry = computed(() =>
      countryItems.value.find(country => country.key === formData.value.countryCode)
    );

    const datacenterItems = computed(() =>
      datacenters.map(datacenter =>
        recommendedDatacenter.value === datacenter.id
          ? {
              ...datacenter,
              name: `${datacenter.name} (${t('recommended').toString()})`,
            }
          : datacenter
      )
    );

    const selectedDatacenter = computed(() =>
      datacenterItems.value.find(datacenter => datacenter.id === formData.value.datacenter)
    );

    const recommendedDatacenter = computed(
      () => DatacenterHelper.getDatacenterFromCountry(formData.value.countryCode).id
    );

    const handleCountryChange = (value: Country) => {
      formData.value.countryCode = value.key;
    };

    const handleDatacenterChange = (value: { id: DatacenterId; label: string }) => {
      formData.value.datacenter = value.id;
    };

    const validateForm = () => {
      formErrorName.value = !formData.value.name ? t('company-name-is-required').toString() : '';
    };

    const handleSubmit = () => {
      validateForm();

      if (formErrorName.value) {
        return;
      }

      context.emit('submit', formData.value);
    };

    onMounted(() => {
      formData.value = {
        ...props.initialFormData,
      };
    });

    return {
      formData,
      formErrorName,
      selectedCountry,
      countryItems,
      datacenterItems,
      recommendedDatacenter,
      handleCountryChange,
      handleDatacenterChange,
      validateForm,
      selectedDatacenter,
      handleSubmit,
    };
  },
});
</script>

<style lang="scss" scoped>
.create-entity-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-100);

  .create-entity-input {
    width: 100%;
  }
}
</style>
